import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, MessageCircle, BookOpen, HelpCircle, Shield, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';

import { useTranslation } from 'react-i18next';

import { useSafeBack } from '@/utils/useSafeBack';
export default function SupportPage() {
  const {
    t
  } = useTranslation();

  const navigate = useNavigate();
  const goBackSafe = useSafeBack('/home');

  return (
    <div className="page-container" style={{ background: 'var(--theme-background, #0f172a)', color: 'var(--theme-text, #f1f5f9)' }}>
      <header className="sticky top-0 z-50 px-4 py-4 shadow-sm backdrop-blur-xl" style={{ background: 'var(--theme-surface, #1e293b)', borderBottom: '1px solid var(--glass-border, #334155)' }}>
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={goBackSafe} className="rounded-full h-9 w-9 p-0" data-testid="support-back-btn">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <HelpCircle className="h-5 w-5" style={{ color: 'var(--theme-accent, #6366f1)' }} />
          <h1 className="text-base font-semibold">{t('generated.supportpage_support_df94')}</h1>
        </div>
      </header>
      <main className="page-content max-w-3xl mx-auto px-4 py-8 pb-20">
        <div className="space-y-8" style={{ lineHeight: 1.75 }}>

          <div className="text-center space-y-3 pb-4">
            <h2 className="text-2xl font-semibold" style={{ fontFamily: 'var(--theme-heading-family)' }}>{t('generated.supportpage_how_can_we_ee47')}</h2>
            <p className="text-sm" style={{ color: 'var(--theme-textSecondary, #94a3b8)' }}>
              {t('generated.supportpage_we_re_here_9a12')}
            </p>
          </div>

          <div className="rounded-xl p-6 space-y-4" style={{ background: 'var(--theme-surface, #1e293b)', border: '1px solid var(--glass-border, #334155)' }}>
            <div className="flex items-center gap-3">
              <Mail className="h-5 w-5 flex-shrink-0" style={{ color: 'var(--theme-accent, #6366f1)' }} />
              <h3 className="font-semibold">{t('generated.supportpage_contact_us_99d6')}</h3>
            </div>
            <p className="text-sm" style={{ color: 'var(--theme-textSecondary, #94a3b8)' }}>
              {t('generated.supportpage_for_any_questions_f075')}
            </p>
            <a
              href="mailto:support@askeili.com"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all hover:opacity-90"
              style={{ background: 'var(--theme-accent, #6366f1)', color: '#fff' }}
              data-testid="support-email-link"
            >
              <Mail className="h-4 w-4" />
              {t('generated.supportpage_support_askeili_com_66cd')}
            </a>
          </div>

          <h3 className="text-lg font-semibold pt-2" style={{ fontFamily: 'var(--theme-heading-family)' }}>{t('generated.supportpage_frequently_asked_questions_8296')}</h3>

          <FaqItem
            icon={<MessageCircle className="h-5 w-5" />}
            question="What is Eili?"
            answer="Eili is a Biblical research companion powered by AI. It helps you explore Scripture with tools like Greek word analysis, historical context, commentary insights, and Gospel-centered reflections — all grounded in verified Bible text."
          />

          <FaqItem
            icon={<BookOpen className="h-5 w-5" />}
            question="What subscription plans are available?"
            answer="Eili offers a Free tier with 25 daily messages, a Premium plan ($9.99/mo) with unlimited messages and all study tools, and a Share plan ($18.99/mo) that includes everything in Premium plus the ability to share your subscription with friends and family."
          />

          <FaqItem
            icon={<HelpCircle className="h-5 w-5" />}
            question="How do I cancel my subscription?"
            answer="You can cancel anytime from the Settings page in the app. If you subscribed via the App Store, manage your subscription in your Apple ID settings. If you subscribed via Google Play, manage it in the Play Store. Web subscriptions can be cancelled directly in the app."
          />

          <FaqItem
            icon={<Shield className="h-5 w-5" />}
            question="Is my data secure?"
            answer="Yes. We use industry-standard encryption for all data in transit. Your conversations and personal data are private and never shared with third parties for advertising. See our Privacy Policy for full details."
          />

          <FaqItem
            icon={<HelpCircle className="h-5 w-5" />}
            question="The app isn't loading or is showing an error"
            answer="Try closing and reopening the app. If the issue persists, check your internet connection. You can also try signing out and back in from the Settings page. If none of these work, email us at support@askeili.com with a description of the issue."
          />

          <FaqItem
            icon={<HelpCircle className="h-5 w-5" />}
            question="How do I restore my subscription on a new device?"
            answer="Sign in with the same account (email or Google) on your new device. Your subscription will sync automatically. For App Store purchases, ensure you're using the same Apple ID."
          />

          <div className="pt-4 space-y-3 text-sm" style={{ color: 'var(--theme-textSecondary, #94a3b8)' }}>
            <div className="flex items-center gap-4">
              <FileText className="h-4 w-4 flex-shrink-0" />
              <a href="/privacy" className="underline hover:opacity-80" data-testid="support-privacy-link">{t('generated.supportpage_privacy_policy_3ef8')}</a>
              <a href="/terms" className="underline hover:opacity-80" data-testid="support-terms-link">{t('generated.supportpage_terms_of_use_9b69')}</a>
            </div>
            <p>{t('generated.supportpage_eili_is_developed_724a')}</p>
            <p>{t('generated.supportpage_app_version_1_1b7d')}</p>
          </div>

        </div>
      </main>
    </div>
  );
}

function FaqItem({ icon, question, answer }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div
      className="rounded-xl overflow-hidden transition-all"
      style={{ background: 'var(--theme-surface, #1e293b)', border: '1px solid var(--glass-border, #334155)' }}
      data-testid={`faq-${question.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 30)}`}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 p-4 text-left transition-colors hover:opacity-90"
      >
        <span style={{ color: 'var(--theme-accent, #6366f1)' }}>{icon}</span>
        <span className="flex-1 text-sm font-medium">{question}</span>
        <span className="text-xs" style={{ color: 'var(--theme-textSecondary)' }}>{open ? '−' : '+'}</span>
      </button>
      {open && (
        <div className="px-4 pb-4 pt-0">
          <p className="text-sm pl-8" style={{ color: 'var(--theme-textSecondary, #94a3b8)', lineHeight: 1.7 }}>{answer}</p>
        </div>
      )}
    </div>
  );
}
